import type { AccountFees, FeeTier } from '@/types/generated/common/account'
import {
  Exchange,
  type Instrument,
  InstrumentKind,
  InstrumentType,
} from '@/types/generated/common/types'

const eq = (a?: string, b?: string): boolean => (a ?? '').toLowerCase() === (b ?? '').toLowerCase()

/**
 * The venue reports fee tiers under ITS symbol (`exchangeSymbol`, e.g. Bybit's "ETHUSDT"), while
 * `symbol` is our normalized one ("ETHUSDT-PERP"). Comparing only `symbol` matched where the two
 * happen to agree (BTCUSD) and silently missed the rest, leaving the fee readout blank.
 */
const matchesInstrumentSymbol = (tierSymbol: string | undefined, inst: Instrument): boolean =>
  eq(tierSymbol, inst.symbol) || (!!inst.exchangeSymbol && eq(tierSymbol, inst.exchangeSymbol))

const isPerpetual = (inst: Instrument): boolean =>
  Boolean(inst.fundingInterval) || !inst.expirationTsMs

/**
 * The venue-specific `FeeTier.instrumentType` string this instrument keys on.
 * Returns null for venues/kinds we can't map.
 */
export function instrumentTypeKey(inst: Instrument): string | null {
  switch (inst.exchange) {
    case Exchange.EXCHANGE_DERIBIT:
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) return 'option'
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) return 'spot'
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE)
        return isPerpetual(inst) ? 'perpetual' : 'future'
      return null
    case Exchange.EXCHANGE_BYBIT:
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) return 'spot'
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) return 'option'
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE)
        return inst.type === InstrumentType.INSTRUMENT_TYPE_INVERSE ? 'inverse' : 'linear'
      return null
    case Exchange.EXCHANGE_HYPERLIQUID:
      return inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT ? 'spot' : 'perpetual'
    case Exchange.EXCHANGE_LIGHTER:
      return inst.kind === InstrumentKind.INSTRUMENT_KIND_SPOT ? 'spot' : 'perpetual'
    default:
      return null
  }
}

/** Does a same-type tier match this instrument's symbol axis (venue-specific)? */
function tierMatchesSymbol(tier: FeeTier, inst: Instrument): boolean {
  switch (inst.exchange) {
    case Exchange.EXCHANGE_DERIBIT:
      // Per-index (perp/option) or per-symbol (dated future). Empty tier symbol = wildcard.
      return (
        !tier.symbol ||
        eq(tier.symbol, inst.priceIndex) ||
        matchesInstrumentSymbol(tier.symbol, inst)
      )
    case Exchange.EXCHANGE_BYBIT:
      if (inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION)
        return !tier.baseCoin || eq(tier.baseCoin, inst.baseCurrency)
      return !tier.symbol || matchesInstrumentSymbol(tier.symbol, inst)
    case Exchange.EXCHANGE_HYPERLIQUID:
      return true // no symbol breakdown
    case Exchange.EXCHANGE_LIGHTER:
      return true // no symbol breakdown
    default:
      return false
  }
}

/**
 * Resolve the effective FeeTier for an instrument from the account's fee schedule.
 * Prefers a symbol-specific tier over a category-level (symbol-less) one. Returns
 * null when nothing matches (caller falls back to instrument base commission).
 */
export function resolveFeeTier(inst: Instrument, fees: AccountFees | null): FeeTier | null {
  if (!fees) return null
  const key = instrumentTypeKey(inst)
  if (!key) return null
  const sameType = fees.tiers.filter((t) => eq(t.instrumentType, key))
  if (sameType.length === 0) return null
  const matches = sameType.filter((t) => tierMatchesSymbol(t, inst))
  if (matches.length === 0) return null
  // Prefer the most specific: a tier that carries a symbol/baseCoin over a wildcard.
  return matches.find((t) => t.symbol || t.baseCoin) ?? matches[0]
}
